/**
 * Color helpers for the user-selected accent color (openspec `user-accent-color`, #68).
 * Pure functions only: no store, no DOM, no side effects.
 */

/** Default (brand) accent color. */
export const DEFAULT_ACCENT = '#b11116';

/** A valid accent is exactly `#rrggbb` (case-insensitive). */
const ACCENT_HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** `true` when `value` is a string shaped like `#rrggbb` (any hex case). */
export function isValidAccentHex(value: unknown): value is string {
  return typeof value === 'string' && ACCENT_HEX_PATTERN.test(value);
}

/** Returns the accent hex normalized to lowercase, or `null` when invalid. */
export function parseAccentHex(value: unknown): string | null {
  if (!isValidAccentHex(value)) {
    return null;
  }

  return value.toLowerCase();
}

/** YIQ brightness threshold: ≥ this, the accent is "light" and needs dark text. */
const YIQ_LIGHT_THRESHOLD = 150;

/**
 * Returns the readable text color to place on top of the given accent:
 * `#ffffff` on dark accents, `#111111` on light accents.
 */
export function onAccentColor(hex: string): '#ffffff' | '#111111' {
  const red = parseInt(hex.slice(1, 3), 16);
  const green = parseInt(hex.slice(3, 5), 16);
  const blue = parseInt(hex.slice(5, 7), 16);

  const yiq = (red * 299 + green * 587 + blue * 114) / 1000;

  return yiq >= YIQ_LIGHT_THRESHOLD ? '#111111' : '#ffffff';
}
